import React, { useEffect } from 'react';
import { styled, alpha } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import GlobeIcon from '@mui/icons-material/LanguageOutlined';
import PhoneIcon from '@mui/icons-material/LocalPhoneOutlined';
import TermsIcon from '@mui/icons-material/GradingOutlined';
import PolicyIcon from '@mui/icons-material/PolicyOutlined';
import { Box, Tooltip, Divider } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { getOrganizationDetails } from '@Reducers/OrganizationSetup';
import { Link } from 'react-router-dom';
import MuiLink from '@mui/material/Link';

// --------------------
// Styled Components
// --------------------

const DetailsContainer = styled('div')(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  padding: theme.spacing(2, 1.5),
  animation: 'fadeIn 0.3s ease-in-out',
  margin: theme.spacing(1),
  borderRadius: '10px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: `0 4px 12px rgba(0, 0, 0, 0.01)`,
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
    borderColor: alpha(theme.palette.primary.main, 0.15),
    boxShadow: `0 6px 16px rgba(0, 0, 0, 0.03)`,
  },

  '@keyframes fadeIn': {
    from: {
      opacity: 0,
      transform: 'translateY(-8px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const OrgName = styled(Box)(({ theme }) => ({
  fontSize: '0.85rem',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
  letterSpacing: '1px',
  wordBreak: 'break-word',
}));

const DetailWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  borderRadius: '6px',
  padding: theme.spacing(0.5, 0.75),
  transition: 'all 0.15s ease',

  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const IconStyled = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  color: theme.palette.text.secondary || '#64748b',
  transition: 'all 0.15s ease',

  svg: {
    width: '16px',
    height: '16px',
  },
}));

const ContentText = styled('span')(({ theme }) => ({
  fontSize: '0.78rem',
  fontWeight: 500,
  color: theme.palette.text.secondary || '#475569',
  wordBreak: 'break-word',
  flex: 1,
  lineHeight: 1.4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
}));

const ActionLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',

  '&:hover': {
    [`& ${ContentText}`]: {
      color: theme.palette.primary.main,
    },
    [`& ${IconStyled}`]: {
      color: theme.palette.primary.main,
    },
  },
}));

const DividerStyled = styled(Divider)(({ theme }) => ({
  width: '100%',
  height: '1px',
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  margin: theme.spacing(1.5, 0),
  borderRadius: '1px',
}));

// --------------------
// Component
// --------------------

function OrganizationContainer() {
  const dispatch = useDispatch<AppDispatch>();
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationData);

  const { name = '', contactInfo, websiteLink } = organizationData || {};

  useEffect(() => {
    dispatch(getOrganizationDetails());
  }, [dispatch]);

  return (
    <DetailsContainer>
      <OrgName>{name || 'Organization'}</OrgName>

      {websiteLink && (
        <DetailWrapper>
          <Tooltip title="Website" arrow>
            <IconStyled>
              <SettingsIcon />
            </IconStyled>
          </Tooltip>

          <Tooltip title={websiteLink} arrow>
            <MuiLink
              href={websiteLink}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                color: 'inherit',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <ContentText>{websiteLink}</ContentText>
            </MuiLink>
          </Tooltip>
        </DetailWrapper>
      )}

      {contactInfo?.supportEmail && (
        <DetailWrapper>
          <Tooltip title="Email" arrow>
            <IconStyled>
              <GlobeIcon />
            </IconStyled>
          </Tooltip>

          <Tooltip title={contactInfo.supportEmail} arrow>
            <ContentText>{contactInfo.supportEmail}</ContentText>
          </Tooltip>
        </DetailWrapper>
      )}

      {name !== 'Cargo Solutions Network' && contactInfo?.telephoneCode && (
        <DetailWrapper>
          <Tooltip title="Phone" arrow>
            <IconStyled>
              <PhoneIcon />
            </IconStyled>
          </Tooltip>

          <Tooltip
            title={`${
              contactInfo.telephoneCode.startsWith('+') ? contactInfo.telephoneCode : `+${contactInfo.telephoneCode}`
            } ${contactInfo.contactNumber}`}
            arrow
          >
            <ContentText>
              {contactInfo.telephoneCode.startsWith('+') ? contactInfo.telephoneCode : `+${contactInfo.telephoneCode}`}{' '}
              {contactInfo.contactNumber}
            </ContentText>
          </Tooltip>
        </DetailWrapper>
      )}

      <DividerStyled />

      <ActionLink to="/terms-and-conditions">
        <DetailWrapper>
          <Tooltip title="Terms and Conditions" arrow>
            <IconStyled>
              <TermsIcon />
            </IconStyled>
          </Tooltip>

          <ContentText>Terms and Conditions</ContentText>
        </DetailWrapper>
      </ActionLink>

      <ActionLink to="/privacy-policy">
        <DetailWrapper>
          <Tooltip title="Privacy Policy" arrow>
            <IconStyled>
              <PolicyIcon />
            </IconStyled>
          </Tooltip>

          <ContentText>Privacy Policy</ContentText>
        </DetailWrapper>
      </ActionLink>
    </DetailsContainer>
  );
}

export default OrganizationContainer;
